import { useCallback, useEffect, useRef, useState } from "react";

// MUI
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { SxProps } from "@mui/material";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";

import { DEFAULT_HEADER_HEIGHT } from "@/Washouts/constants";
import { generateSx, getRowClassName, getRowId } from "@/Layouts/components/Tables/DataSmartTable/helpers";
import { useMyCitContextData } from "@/DataPages/context/myCit/MyCitContext";
import { useMyCitTableConfiguration } from "./hooks/useMyCitTableConfiguration";
import { IDeal } from "@/Common/types/MyCit/model";

export default function MyCITDataTable() {
	const { deals, setFilteredDeals, filterModel, setFilterModel } = useMyCitContextData();

	const apiRef = useGridApiRef();

	const prevCurrent = useRef<string[]>([]);

	const { columns, pinnedColumns, slots, columnVisibilityModel, initialState, slotProps, customProps } = useMyCitTableConfiguration();

	const onStateChange = useCallback(() => {
		if (!apiRef.current) {
			return;
		}

		const filterState = apiRef.current.state.filter;

		let currentGroupIds: string[] = [];
		const filtered = filterState.filteredRowsLookup;

		if (filtered) {
			const rowModels = apiRef.current.state.rows.dataRowIdToModelLookup;
			let allDeals = apiRef.current.state.rows.dataRowIds.map((id) => rowModels[id] as IDeal);
			currentGroupIds = allDeals.filter((deal) => filtered[deal._id]).map((deal) => deal._id);
		}

		if (JSON.stringify(prevCurrent?.current) !== JSON.stringify(currentGroupIds)) {
			prevCurrent.current = currentGroupIds;
			setFilteredDeals(prevCurrent.current);
		}
	}, []);

	useEffect(() => {
		if (!apiRef.current?.subscribeEvent) {
			return () => {};
		}

		const clearListener1 = apiRef.current?.subscribeEvent("filterModelChange", onStateChange);
		const clearListener2 = apiRef.current?.subscribeEvent("rowCountChange", onStateChange);

		onStateChange();

		return () => {
			clearListener1();
			clearListener2();
		};
	}, [deals]);

	const [sx, setSx] = useState<SxProps>({});

	useEffect(() => {
		setSx(generateSx(apiRef));

		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [deals, apiRef]);

	return (
		<DataSmartTable
			apiRef={apiRef}
			autoHeight={false}
			columns={columns}
			disableColumnMenu
			disableMultipleRowSelection
			disableRowSelectionOnClick
			getRowClassName={getRowClassName}
			getRowId={getRowId}
			columnVisibilityModel={columnVisibilityModel}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			hideFooter
			initialState={initialState}
			pinnedColumns={pinnedColumns}
			rowHeight={25}
			rows={deals}
			sx={sx}
			customProps={customProps}
			slotProps={slotProps}
			slots={slots}
			enableCustomScrollbars
			maxRows={20}
			filterModel={filterModel}
			onFilterModelChange={setFilterModel}
		/>
	);
}
